/**
 * Builds scanner filter entities from simple AI tool parameters.
 *
 * The AI sends flat, readable parameters. This builder validates
 * column types, operators, and values, then produces the correct
 * QueryFilterGroup format that the scanner expects.
 */

import { OPERATORS_BY_DATA_TYPE } from '@/features/sliced-form/schemas/filter-entity'
import type { ColumnCatalogDef } from '@/types/column-catalog'

/** What the AI sends */
export interface AIFilterInput {
  left: {
    type: 'column'
    column: string
  }
  operator: string
  right: {
    type: 'value' | 'column'
    value?: unknown
    column?: string
  }
}

/** Column type info from the live state registry */
export interface ColumnTypeInfo {
  dataType: string
  enumOptions?: string[]
}

/**
 * The filterable keys of a column catalog and their types, as `buildFilters`
 * takes them: what a panel publishes live, for a panel or page that does not.
 * Display-only columns (a relative date) are shown, never filtered on.
 */
export function filterColumnsOf(columns: Record<string, ColumnCatalogDef>): {
  keys: string[]
  types: Record<string, ColumnTypeInfo>
} {
  const defs = Object.values(columns).filter((c) => !c.displayOnly)
  return {
    keys: defs.map((c) => c.key),
    types: Object.fromEntries(
      defs.map((c): [string, ColumnTypeInfo] => [
        c.key,
        { dataType: c.dataType, enumOptions: c.enumOptions?.map((o) => o.value) },
      ]),
    ),
  }
}

/** Scanner filter leaf in QueryFilterGroup format */
interface FilterLeaf {
  left: { column: string }
  operator: string
  right: unknown
  rightType: 'value' | 'column' | 'time_slices'
  enabled: boolean
}

interface QueryFilterGroup {
  AND?: FilterLeaf[]
  OR?: FilterLeaf[]
}

export interface FilterBuildResult {
  success: true
  filters: QueryFilterGroup
}

export interface FilterBuildError {
  success: false
  error: string
}

/**
 * Valid operators per data type — the SAME canonical table the filter validator
 * enforces (`OPERATORS_BY_DATA_TYPE`), not a local copy. The old inline copy had
 * drifted (it wrongly allowed `eq`/`neq` on `enum` columns, which the real
 * validator rejects). Cast to a string-indexed shape so an unknown dataType
 * looks up to `undefined` (validation skipped), preserving prior behavior.
 */
const OPERATORS_BY_TYPE = OPERATORS_BY_DATA_TYPE as Record<string, readonly string[]>

const NUMERIC_TYPES = new Set(['number', 'percent', 'currency', 'volume'])

/**
 * Build a valid QueryFilterGroup from simple AI inputs.
 * Validates column keys, operators, and value types.
 */
export function buildFilters(
  inputs: AIFilterInput[],
  validColumns?: string[],
  columnTypeMap?: Record<string, ColumnTypeInfo>,
): FilterBuildResult | FilterBuildError {
  if (!inputs || inputs.length === 0) {
    return { success: false, error: 'At least one filter is required' }
  }

  const validSet = validColumns ? new Set(validColumns) : null
  const entities: FilterLeaf[] = []

  for (const input of inputs) {
    // ── Validate left column ──
    if (!input.left?.column) {
      return { success: false, error: 'Filter missing left column' }
    }
    const col = input.left.column
    if (validSet && !validSet.has(col)) {
      return {
        success: false,
        error: `Invalid column "${col}". Use search_knowledge(domain="column") to find the correct key.`,
      }
    }

    // ── Validate operator ──
    if (!input.operator) {
      return { success: false, error: `Filter for "${col}" missing operator` }
    }

    const colInfo = columnTypeMap?.[col]
    if (colInfo) {
      const allowedOps = OPERATORS_BY_TYPE[colInfo.dataType]
      if (allowedOps && !allowedOps.includes(input.operator)) {
        return {
          success: false,
          error: `Column "${col}" is type "${colInfo.dataType}" — operator "${input.operator}" is not valid. Use: ${allowedOps.join(', ')}`,
        }
      }
    }

    // ── Validate right side ──
    if (!input.right) {
      return { success: false, error: `Filter for "${col}" missing right value` }
    }

    if (input.right.type === 'value' && colInfo) {
      const val = input.right.value

      // Numeric columns need number values
      if (NUMERIC_TYPES.has(colInfo.dataType)) {
        if (input.operator === 'between') {
          if (!Array.isArray(val) || (val as unknown[]).length !== 2) {
            return {
              success: false,
              error: `Filter for "${col}" with "between" needs [low, high] array.`,
            }
          }
        } else if (input.operator !== 'is_true' && input.operator !== 'is_false') {
          if (typeof val !== 'number') {
            return {
              success: false,
              error: `Column "${col}" is numeric — value must be a number, got ${typeof val}.`,
            }
          }
        }
      }

      // Enum columns: validate values against allowed options
      if (colInfo.dataType === 'enum' && colInfo.enumOptions) {
        const validOptions = new Set(colInfo.enumOptions)
        if (input.operator === 'in' || input.operator === 'not_in') {
          if (!Array.isArray(val)) {
            return {
              success: false,
              error: `Column "${col}" with "${input.operator}" needs an array of values.`,
            }
          }
          const invalid = (val as string[]).filter((v) => !validOptions.has(v))
          if (invalid.length > 0) {
            return {
              success: false,
              error: `Invalid values for "${col}": ${invalid.join(', ')}. Valid options: ${colInfo.enumOptions.join(', ')}`,
            }
          }
        } else if (input.operator === 'eq' || input.operator === 'neq') {
          if (typeof val === 'string' && !validOptions.has(val)) {
            return {
              success: false,
              error: `Invalid value "${val}" for "${col}". Valid options: ${colInfo.enumOptions.join(', ')}`,
            }
          }
        }
      }

      // String columns need string values
      if (colInfo.dataType === 'string' && input.operator !== 'in' && input.operator !== 'not_in') {
        if (typeof val !== 'string' && val != null) {
          return { success: false, error: `Column "${col}" is a string — value must be a string.` }
        }
      }
    }

    // Validate right column exists
    if (input.right.type === 'column') {
      if (!input.right.column) {
        return { success: false, error: `Filter for "${col}" — right column is empty.` }
      }
      if (validSet && !validSet.has(input.right.column)) {
        return { success: false, error: `Invalid right column "${input.right.column}".` }
      }
    }

    // ── Build the entity in QueryFilterGroup leaf format ──
    let rightValue: unknown
    if (input.right.type === 'column') {
      rightValue = { column: input.right.column ?? '' }
    } else if (input.operator === 'between' && Array.isArray(input.right.value)) {
      rightValue = { value: input.right.value }
    } else if (input.operator === 'is_true' || input.operator === 'is_false') {
      rightValue = { value: input.operator === 'is_true' }
    } else if (input.operator === 'in' || input.operator === 'not_in') {
      // Wrap array in { value: [...] }
      rightValue = { value: input.right.value ?? [] }
    } else {
      rightValue = { value: input.right.value ?? null }
    }

    entities.push({
      left: { column: col },
      operator: input.operator,
      right: rightValue,
      rightType: input.right.type,
      enabled: true,
    })
  }

  return { success: true, filters: { AND: entities } }
}
